import { Box, Typography, Button, Paper, Container, Stack, useTheme, alpha } from '@mui/material';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Logo } from '@/components/atoms/Logo';
import BugReportIcon from '@mui/icons-material/BugReport';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';

export function ErrorPage() {
  const navigate = useNavigate();
  const error = useRouteError();
  const theme = useTheme();

  let errorMessage = 'An unexpected error occurred.';
  let errorStatus: string | number = 'Error';

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || error.data?.message || errorMessage;
    errorStatus = error.status;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  const isDynamicImportError = errorMessage.includes('loading dynamically imported module') || 
                               errorMessage.includes('Failed to fetch dynamically imported module');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
        p: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(10px)',
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            boxShadow: `0 24px 48px -12px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ mb: 4, mx: 'auto' }}><Logo variant="icon" height={64} /></Box>

          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: 'error.main',
              mb: 3,
            }}
          >
            <BugReportIcon sx={{ fontSize: 48 }} />
          </Box>

          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: 'text.primary' }}>
            Oops! Something went wrong
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            {isDynamicImportError 
              ? "We couldn't load the requested page module. This might be due to a network issue or a recent update. Please try refreshing the page."
              : errorMessage}
          </Typography>

          {errorStatus && (
             <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                mb: 4, 
                fontFamily: 'monospace', 
                bgcolor: alpha(theme.palette.text.disabled, 0.05),
                p: 1,
                borderRadius: 1
              }}
            >
              Status: {errorStatus}
            </Typography>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{ 
                px: 4, 
                py: 1.5, 
                borderRadius: 2,
                boxShadow: theme.shadows[4]
              }}
            >
              Refresh Page
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{ px: 4, py: 1.5, borderRadius: 2 }}
            >
              Go to Dashboard
            </Button>
          </Stack>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 4 }}>
            If the problem persists, please contact support.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
